/**
 * DealWaterfall — interactive financed-deal pricing widget (Wave 14)
 *
 * Renders a persisted deal's pricing waterfall and lets the user drag the
 * Palo Alto discount and each channel margin tier; everything recomputes
 * CLIENT-SIDE instantly (no AI round-trip). The math mirrors the backend
 * deal-engine exactly: PA list × (1-disc) = net, then tiers compound
 * sequentially (e.g. 3% Synnex → 12% NWN → 5% GS $Mart) = customer total;
 * annual payment = total / term.
 *
 * "Save" routes the adjusted numbers through the action dispatcher →
 * update_deal tool, so the database deal stays the source of truth.
 */
import React, { useMemo, useState, useCallback } from 'react';
import { RotateCcw, Save, TrendingDown, TrendingUp } from 'lucide-react';
import './DealWaterfall.css';

const num = (v) => Number(v) || 0;
const round2 = (v) => Math.round(v * 100) / 100;

function fmtMoney(v) {
  const x = num(v);
  if (Math.abs(x) >= 1e9) return `$${(x / 1e9).toFixed(2)}B`;
  if (Math.abs(x) >= 1e6) return `$${(x / 1e6).toFixed(2)}M`;
  if (Math.abs(x) >= 1e3) return `$${(x / 1e3).toFixed(0)}K`;
  return `$${x.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtExact(v) {
  return `$${num(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Mirror of backend deal-engine computeWaterfall */
function compute(lines, tiers, dealDiscount, termYears) {
  const priced = (lines || []).map(l => {
    const listTotal = num(l.qty) * num(l.unit_list_price);
    const disc = (l.discount_pct === null || l.discount_pct === undefined) ? dealDiscount : num(l.discount_pct);
    return { ...l, list_total: round2(listTotal), applied_discount_pct: disc, net_total: round2(listTotal * (1 - disc / 100)) };
  });
  const listTotal = round2(priced.reduce((s, l) => s + l.list_total, 0));
  const netTotal = round2(priced.reduce((s, l) => s + l.net_total, 0));

  let running = netTotal;
  const waterfall = [{ label: 'Palo Alto Net', markup_pct: null, amount: netTotal, uplift: null }];
  for (const t of tiers) {
    const next = round2(running * (1 + num(t.markup_pct) / 100));
    waterfall.push({ label: t.label, markup_pct: num(t.markup_pct), amount: next, uplift: round2(next - running) });
    running = next;
  }
  const stackMultiplier = tiers.reduce((m, t) => m * (1 + num(t.markup_pct) / 100), 1);

  return {
    lines: priced,
    list_total: listTotal,
    pa_net_total: netTotal,
    waterfall,
    stack_multiplier: stackMultiplier,
    customer_total: running,
    annual_payment: termYears > 0 ? round2(running / termYears) : running,
  };
}

export default function DealWaterfall({ deal = {}, lines = [], tiers = [], bau = null, assumptions = [], onAction }) {
  const saved = {
    discount: num(deal.pa_discount_pct),
    tiers: tiers.map(t => num(t.markup_pct)),
    term: num(deal.term_years) || 5,
  };

  const [discount, setDiscount] = useState(saved.discount);
  const [tierPcts, setTierPcts] = useState(saved.tiers);
  const [term, setTerm] = useState(saved.term);

  const liveTiers = tiers.map((t, i) => ({ ...t, markup_pct: tierPcts[i] ?? num(t.markup_pct) }));
  const c = useMemo(() => compute(lines, liveTiers, discount, term), [lines, tierPcts, discount, term]);

  const dirty = discount !== saved.discount
    || term !== saved.term
    || tierPcts.some((p, i) => p !== saved.tiers[i]);

  const bauTermTotal = bau ? num(bau.annual_spend) * term : null;
  const delta = bau ? round2(bauTermTotal - c.customer_total) : null;

  const handleReset = useCallback(() => {
    setDiscount(saved.discount);
    setTierPcts(saved.tiers);
    setTerm(saved.term);
  }, [saved.discount, saved.term]);

  const handleSave = useCallback(() => {
    if (!deal.deal_id || !onAction) return;
    const tierDesc = liveTiers.map(t => `${t.label} ${t.markup_pct}%`).join(', ');
    onAction({
      type: 'query',
      query: `Update deal ${deal.deal_id}: set pa_discount_pct to ${discount}, term_years to ${term}, and margin tiers to [${tierDesc}].`,
    });
  }, [deal.deal_id, discount, term, liveTiers, onAction]);

  const maxBar = Math.max(c.customer_total, bauTermTotal || 0) || 1;

  return (
    <div className="dw">
      {/* Header */}
      <div className="dw-header">
        <div>
          <div className="dw-title">{deal.name}</div>
          <div className="dw-subtitle">
            {term}-year financed {deal.status || 'deal'}
            {deal.department_label ? ` — ${deal.department_label}` : ''}
          </div>
        </div>
        <div className="dw-header-actions">
          {dirty && (
            <>
              <button className="dw-btn" onClick={handleReset} title="Reset to saved values">
                <RotateCcw size={13} /> Reset
              </button>
              <button className="dw-btn dw-btn-primary" onClick={handleSave} title="Save these numbers to the deal">
                <Save size={13} /> Save
              </button>
            </>
          )}
        </div>
      </div>

      {/* Headline stats */}
      <div className="dw-stats">
        <div className="dw-stat">
          <span className="dw-stat-value">{fmtMoney(c.list_total)}</span>
          <span className="dw-stat-label">PA List</span>
        </div>
        <div className="dw-stat">
          <span className="dw-stat-value dw-accent">{fmtMoney(c.pa_net_total)}</span>
          <span className="dw-stat-label">PA Net ({discount}% off)</span>
        </div>
        <div className="dw-stat">
          <span className="dw-stat-value dw-warn">{fmtMoney(c.customer_total)}</span>
          <span className="dw-stat-label">Customer Total</span>
        </div>
        <div className="dw-stat">
          <span className="dw-stat-value dw-success">{fmtMoney(c.annual_payment)}</span>
          <span className="dw-stat-label">Annual Payment</span>
        </div>
      </div>

      {/* PA discount slider */}
      <div className="dw-control">
        <div className="dw-control-head">
          <span>Palo Alto Discount</span>
          <span className="dw-control-value">{discount}%</span>
        </div>
        <input
          type="range" min="0" max="70" step="0.5"
          value={discount}
          onChange={e => setDiscount(Number(e.target.value))}
          className="dw-slider"
          aria-label="Palo Alto discount percent"
        />
      </div>

      {/* Margin tiers */}
      <div className="dw-tiers">
        {liveTiers.map((t, i) => (
          <div className="dw-control dw-tier" key={i}>
            <div className="dw-control-head">
              <span>{t.label}</span>
              <span className="dw-control-value">+{t.markup_pct}%</span>
            </div>
            <input
              type="range" min="0" max="20" step="0.5"
              value={t.markup_pct}
              onChange={e => setTierPcts(prev => prev.map((p, j) => j === i ? Number(e.target.value) : p))}
              className="dw-slider dw-slider-tier"
              aria-label={`${t.label} markup percent`}
            />
          </div>
        ))}
        <div className="dw-stack-note">
          Channel stack: ×{c.stack_multiplier.toFixed(4)} ({((c.stack_multiplier - 1) * 100).toFixed(1)}% on top of PA net)
        </div>
      </div>

      {/* Term */}
      <div className="dw-control">
        <div className="dw-control-head">
          <span>Term</span>
          <span className="dw-control-value">{term} years</span>
        </div>
        <div className="dw-term-buttons">
          {[3, 4, 5, 7].map(y => (
            <button
              key={y}
              className={`dw-term-btn ${term === y ? 'dw-term-btn-active' : ''}`}
              onClick={() => setTerm(y)}
            >
              {y}yr
            </button>
          ))}
        </div>
      </div>

      {/* Waterfall */}
      <div className="dw-waterfall">
        {c.waterfall.map((w, i) => (
          <div className="dw-wf-row" key={i}>
            <div className="dw-wf-label">
              {w.label}
              {w.markup_pct !== null && <span className="dw-wf-pct">+{w.markup_pct}%</span>}
            </div>
            <div className="dw-wf-track">
              <div className="dw-wf-bar" style={{ width: `${(w.amount / maxBar) * 100}%` }} />
            </div>
            <div className="dw-wf-amount">{fmtExact(w.amount)}</div>
          </div>
        ))}
      </div>

      {/* BAU comparison */}
      {bau && (
        <div className="dw-bau">
          <div className="dw-bau-head">vs. {bau.label}</div>
          <div className="dw-wf-row">
            <div className="dw-wf-label">BAU over {term} years</div>
            <div className="dw-wf-track">
              <div className="dw-wf-bar dw-wf-bar-bau" style={{ width: `${(bauTermTotal / maxBar) * 100}%` }} />
            </div>
            <div className="dw-wf-amount">{fmtExact(bauTermTotal)}</div>
          </div>
          <div className={`dw-delta ${delta >= 0 ? 'dw-delta-save' : 'dw-delta-cost'}`}>
            {delta >= 0 ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
            {delta >= 0
              ? `Platform saves ${fmtMoney(Math.abs(delta))} over ${term} years (${fmtMoney(Math.abs(delta) / term)}/yr)`
              : `Platform costs ${fmtMoney(Math.abs(delta))} more over ${term} years`}
          </div>
        </div>
      )}

      {/* BOM */}
      <details className="dw-bom">
        <summary>BOM — {lines.length} line{lines.length === 1 ? '' : 's'}</summary>
        <table className="dw-bom-table">
          <thead>
            <tr><th>SKU</th><th>Product</th><th className="dw-r">Qty</th><th className="dw-r">List</th><th className="dw-r">Net</th></tr>
          </thead>
          <tbody>
            {c.lines.map((l, i) => (
              <tr key={i}>
                <td className="dw-mono">{l.sku || '—'}</td>
                <td>{l.product_name}</td>
                <td className="dw-r">{l.qty}</td>
                <td className="dw-r">{fmtMoney(l.list_total)}</td>
                <td className="dw-r">{fmtMoney(l.net_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>

      {/* Assumptions */}
      {assumptions.length > 0 && (
        <details className="dw-assumptions">
          <summary>Assumptions — {assumptions.length}</summary>
          <ul>{assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </details>
      )}
    </div>
  );
}
